import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import Swal from 'sweetalert2';

import { JobService }       from '../services/job.service';
import { TaskLog }          from '../models/task-log.model';
import { UserService }      from '../../users/services/user.service';
import { Worker }           from '../../users/models/worker.model';
import { DashboardService } from '../../dashboard/services/dashboard.service';
import { ExcelService }     from 'src/app/core/services/excel.service';
import { PdfService }       from 'src/app/core/services/pdf.service';
import { SignalRService }   from 'src/app/core/services/signalr.service';
import { SubscriptionService } from 'src/app/core/services/subscription.service';

@Component({
  selector:    'app-job-logs',
  templateUrl: './job-logs.component.html',
  styleUrls:   ['./job-logs.component.css']
})
export class JobLogsComponent implements OnInit {

  jobId!: string;
  logs: TaskLog[]      = [];
  pagedLogs: TaskLog[] = [];
  workers: Worker[]    = [];
  loading              = true;
  exportingPdf         = false;  // ✅ separate loading state per button

  private presenceMap: { [userId: string]: boolean } = {};

  selectedPhotoUrl: string | null = null;

  isPlaying     = false;
  playbackIndex = 0;
  playbackMarker?: L.Marker;
  playbackInterval: any;

  logPage       = 1;
  logsPerPage   = 6;
  totalLogPages = 1;

  private map!: L.Map;
  private markerCluster!: L.MarkerClusterGroup;
  private polyline?: L.Polyline;
  private liveMarkers: { [userId: string]: L.Marker } = {};

  private avatarColors = [
    'linear-gradient(135deg, #1F3A5F, #2BB0A6)',
    'linear-gradient(135deg, #2BB0A6, #1F8F88)',
    'linear-gradient(135deg, #3498DB, #1F3A5F)',
    'linear-gradient(135deg, #F5A623, #E8821A)',
    'linear-gradient(135deg, #2ECC71, #1A9E4C)',
    'linear-gradient(135deg, #9B59B6, #6C3483)',
    'linear-gradient(135deg, #E74C3C, #C0392B)',
    'linear-gradient(135deg, #1ABC9C, #148F77)',
  ];

  constructor(
    private route:               ActivatedRoute,
    private router:              Router,
    private jobService:          JobService,
    private userService:         UserService,
    private dashboardService:    DashboardService,
    private pdfService:          PdfService,
    private excelService:        ExcelService,
    private signalR:             SignalRService,
    private subscriptionService: SubscriptionService  // ✅ new
  ) {}

  ngOnInit(): void {
    this.jobId = this.route.snapshot.paramMap.get('id')!;
    this.initMap();
    this.userService.getWorkers().subscribe(w => { this.workers = w; });
    this.loadLogs();
    this.loadPresence();

    this.signalR.startConnection().then(() => {
      this.signalR.on('WorkerOnline',  (data: any) => {
        this.presenceMap[data.userId] = true;
      });
      this.signalR.on('WorkerOffline', (data: any) => {
        this.presenceMap[data.userId] = false;
      });
      this.signalR.onLocationUpdate((data: any) => {
        this.addLiveMarker(data);
      });
    });
  }

  loadPresence(): void {
    this.dashboardService.getLiveWorkers().subscribe({
      next: (liveWorkers) => {
        liveWorkers.forEach(lw => {
          this.presenceMap[lw.userId] = lw.isOnline;
        });
      },
      error: () => {}
    });
  }

  // ── Summary ──────────────────────────────────────

  get totalHours(): number {
    return this.logs.reduce((sum, l) => sum + (l.hoursSpent || 0), 0);
  }

  get uniqueWorkers(): number {
    return new Set(this.logs.map(l => l.userId)).size;
  }

  get photoCount(): number {
    return this.logs.reduce((sum, l) => sum + (l.photos?.length || 0), 0);
  }

  // ── Worker helpers ────────────────────────────────

  getWorker(userId: string): Worker | undefined {
    return this.workers.find(w => w.id === userId);
  }

  getWorkerName(userId: string): string {
    return this.getWorker(userId)?.fullName ?? 'Unknown';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    const code = userId?.charCodeAt(0) ?? 0;
    return this.avatarColors[code % this.avatarColors.length];
  }

  getWorkerStatus(userId: string): 'active' | 'offline' {
    return this.presenceMap[userId] === true ? 'active' : 'offline';
  }

  // ── Map ───────────────────────────────────────────

  createWorkerIcon(worker?: Worker): L.DivIcon {
    const status      = this.getWorkerStatus(worker?.id || '');
    const statusColor = status === 'active' ? '#2ECC71' : '#E74C3C';
    const initials    = this.getInitials(worker?.fullName || '');

    return L.divIcon({
      className: '',
      html: worker?.photoUrl
        ? `<div style="position:relative;width:44px;height:44px;">
             <div style="position:absolute;width:44px;height:44px;border-radius:50%;
                         border:3px solid ${statusColor};"></div>
             <img src="${worker.photoUrl}" onerror="this.style.display='none'"
                  style="width:38px;height:38px;border-radius:50%;position:absolute;
                         top:3px;left:3px;object-fit:cover;"/>
           </div>`
        : `<div style="width:44px;height:44px;border-radius:50%;
                       background:linear-gradient(135deg,#1F3A5F,#2BB0A6);
                       color:white;display:flex;align-items:center;justify-content:center;
                       font-weight:700;font-size:13px;border:3px solid ${statusColor};
                       font-family:'Syne',sans-serif;">
             ${initials}
           </div>`,
      iconSize:   [44, 44],
      iconAnchor: [22, 22]
    });
  }

  initMap(): void {
    this.map = L.map('logsMap').setView([6.5244, 3.3792], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(this.map);
    this.markerCluster = L.markerClusterGroup();
    this.map.addLayer(this.markerCluster);
    setTimeout(() => this.map.invalidateSize(), 300);
  }

  loadLogs(): void {
    this.jobService.getJobLogs(this.jobId).subscribe({
      next: (data) => {
        this.logs    = data;
        this.loading = false;
        this.buildPagination();
        this.updatePagedLogs();
        this.renderMarkers();
      },
      error: () => { this.loading = false; }
    });
  }

  buildPagination(): void {
    this.totalLogPages =
      Math.max(1, Math.ceil(this.logs.length / this.logsPerPage));
  }

  updatePagedLogs(): void {
    const start    = (this.logPage - 1) * this.logsPerPage;
    this.pagedLogs = this.logs.slice(start, start + this.logsPerPage);
    this.buildPagination();
  }

  renderMarkers(): void {
    this.markerCluster.clearLayers();
    const latLngs: L.LatLngExpression[] = [];

    this.logs.forEach(log => {
      if (log.latitude == null || log.longitude == null) return;
      const latLng: L.LatLngExpression = [log.latitude, log.longitude];
      latLngs.push(latLng);

      const icon   = this.createWorkerIcon(this.getWorker(log.userId));
      const marker = L.marker(latLng, { icon }).bindPopup(`
        <div style="min-width:180px;font-family:'DM Sans',sans-serif;">
          <strong style="color:#1F3A5F">${this.getWorkerName(log.userId)}</strong><br/>
          <small style="color:#6B7280">${new Date(log.createdUtc).toLocaleString()}</small><br/><br/>
          <span style="color:#444">${log.workDone || log.notes || 'No notes'}</span><br/>
          <span style="background:rgba(43,176,166,.12);color:#1F8F88;
                       padding:2px 8px;border-radius:12px;font-size:11px;font-weight:700;">
            ${log.hoursSpent}h
          </span>
        </div>`);
      this.markerCluster.addLayer(marker);
    });

    if (this.polyline) this.map.removeLayer(this.polyline);

    if (latLngs.length > 0) {
      this.polyline = L.polyline(latLngs, {
        color: '#2BB0A6', weight: 3, opacity: 0.7, dashArray: '6 4'
      }).addTo(this.map);
      this.map.fitBounds(this.polyline.getBounds(), { padding: [32, 32] });
    }
  }

  addLiveMarker(data: any): void {
    if (!data.latitude || !data.longitude) return;
    const latLng: L.LatLngExpression = [data.latitude, data.longitude];
    const icon = this.createWorkerIcon(this.getWorker(data.userId));

    if (this.liveMarkers[data.userId]) {
      this.liveMarkers[data.userId].setLatLng(latLng).setIcon(icon);
    } else {
      const marker = L.marker(latLng, { icon })
        .addTo(this.map)
        .bindPopup(`<strong>${data.workerName}</strong><br/><small>Live tracking</small>`);
      this.liveMarkers[data.userId] = marker;
    }

    this.map.panTo(latLng);
  }

  focusLog(log: TaskLog): void {
    if (log.latitude == null || log.longitude == null) return;
    this.map.setView([log.latitude, log.longitude], 16);
  }

  startPlayback(): void {
    if (!this.logs.length) return;
    this.isPlaying     = true;
    this.playbackIndex = 0;

    const path = [...this.logs]
      .filter(l => l.latitude && l.longitude)
      .sort((a, b) =>
        new Date(a.createdUtc).getTime() - new Date(b.createdUtc).getTime());

    if (!path.length) return;
    if (this.playbackMarker) this.map.removeLayer(this.playbackMarker);

    const icon = L.divIcon({
      className: '',
      html: `<div style="width:14px;height:14px;border-radius:50%;
                         background:#2BB0A6;border:2px solid #fff;
                         box-shadow:0 0 0 3px rgba(43,176,166,0.35);"></div>`,
      iconSize:   [14, 14],
      iconAnchor: [7, 7]
    });

    this.playbackMarker = L.marker(
      [path[0].latitude, path[0].longitude], { icon }
    ).addTo(this.map);

    this.playbackInterval = setInterval(() => {
      if (this.playbackIndex >= path.length) { this.stopPlayback(); return; }
      const log = path[this.playbackIndex];
      this.playbackMarker!.setLatLng([log.latitude, log.longitude]);
      this.map.panTo([log.latitude, log.longitude]);
      this.playbackIndex++;
    }, 1000);
  }

  stopPlayback(): void {
    this.isPlaying = false;
    clearInterval(this.playbackInterval);
  }

  // ── Exports ───────────────────────────────────────

// ✅ No changes needed — same call, backend does the work now
async exportPdf(): Promise<void> {
  if (this.exportingPdf) return;
  this.exportingPdf = true;

  try {
    await this.pdfService.generateJobLogsPdf(
      this.jobId,
      this.logs,
      this.getWorkerName.bind(this)
    );
  } catch (err) {
    console.error('PDF export failed:', err);
    await Swal.fire({
      title:             'Export Failed',
      text:              'PDF could not be generated. Please try again.',
      icon:              'error',
      confirmButtonColor: '#2BB0A6',
      customClass: { popup: 'swal-fieldops' }
    });
  } finally {
    this.exportingPdf = false;
  }
}
  async exportExcel(): Promise<void> {
    await this.excelService.exportJobLogs(
      this.jobId,
      this.logs,
      this.getWorkerName.bind(this)
    );
  }

  // ── Photo lightbox ────────────────────────────────

  openPhoto(url: string): void  { this.selectedPhotoUrl = url; }
  closePhoto(): void            { this.selectedPhotoUrl = null; }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    console.warn('Photo failed to load:', img.src);
  }
}